import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Send, Bot, User, Loader2, ArrowLeft, ImageIcon, Sparkles, MessageSquare, Plus, Trash2, Camera, Image as ImageLucide, Video } from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';

type Message = { 
  id?: string;
  role: 'user' | 'assistant'; 
  content: string;
  image?: string;
  video?: string; // Video generated by AI
  userImage?: string; // Image sent by user
};

type Conversation = {
  id: string;
  title: string;
  created_at: string;
};

const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-chat`;

async function getAuthToken(): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token ?? null;
}

async function streamChat({
  messages,
  onDelta,
  onDone,
  onError,
}: {
  messages: Message[];
  onDelta: (deltaText: string) => void;
  onDone: () => void;
  onError: (error: string) => void;
}) {
  const token = await getAuthToken();
  if (!token) {
    onError('Non autenticato. Effettua il login per continuare.');
    return;
  }

  const resp = await fetch(CHAT_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ messages: messages.map(m => ({ role: m.role, content: m.content, image: m.userImage })) }),
  });

  if (!resp.ok) {
    const errorData = await resp.json().catch(() => ({ error: 'Unknown error' }));
    onError(errorData.error || `Error: ${resp.status}`);
    return;
  }

  if (!resp.body) {
    onError("Failed to start stream");
    return;
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let textBuffer = "";
  let streamDone = false;

  while (!streamDone) {
    const { done, value } = await reader.read();
    if (done) break;
    textBuffer += decoder.decode(value, { stream: true });

    let newlineIndex: number;
    while ((newlineIndex = textBuffer.indexOf("\n")) !== -1) {
      let line = textBuffer.slice(0, newlineIndex);
      textBuffer = textBuffer.slice(newlineIndex + 1);

      if (line.endsWith("\r")) line = line.slice(0, -1);
      if (line.startsWith(":") || line.trim() === "") continue;
      if (!line.startsWith("data: ")) continue;

      const jsonStr = line.slice(6).trim();
      if (jsonStr === "[DONE]") {
        streamDone = true;
        break;
      }

      try {
        const parsed = JSON.parse(jsonStr);
        const content = parsed.choices?.[0]?.delta?.content as string | undefined;
        if (content) onDelta(content);
      } catch {
        textBuffer = line + "\n" + textBuffer;
        break;
      }
    }
  }

  if (textBuffer.trim()) {
    for (let raw of textBuffer.split("\n")) {
      if (!raw) continue;
      if (raw.endsWith("\r")) raw = raw.slice(0, -1);
      if (raw.startsWith(":") || raw.trim() === "") continue;
      if (!raw.startsWith("data: ")) continue;
      const jsonStr = raw.slice(6).trim();
      if (jsonStr === "[DONE]") continue;
      try {
        const parsed = JSON.parse(jsonStr);
        const content = parsed.choices?.[0]?.delta?.content as string | undefined;
        if (content) onDelta(content);
      } catch { /* ignore */ }
    }
  }

  onDone();
}

async function generateImage(prompt: string): Promise<{ image?: string; text?: string }> {
  const token = await getAuthToken();
  if (!token) {
    throw new Error("Non autenticato. Effettua il login per continuare.");
  }

  const resp = await fetch(CHAT_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ generateImage: prompt }),
  });

  if (!resp.ok) {
    throw new Error("Failed to generate image");
  }

  return resp.json();
}

async function generateVideo(prompt: string): Promise<{ image?: string; text?: string }> {
  const token = await getAuthToken();
  if (!token) {
    throw new Error("Non autenticato. Effettua il login per continuare.");
  }

  const resp = await fetch(CHAT_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ generateVideo: prompt }),
  });

  if (!resp.ok) {
    throw new Error("Failed to generate video");
  }

  return resp.json();
}

interface AIChatbotProps {
  onBack?: () => void;
}

const AIChatbot: React.FC<AIChatbotProps> = ({ onBack }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [isGeneratingVideo, setIsGeneratingVideo] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [showConversations, setShowConversations] = useState(false);
  const [isLoadingConversations, setIsLoadingConversations] = useState(false);
  const [pendingImage, setPendingImage] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  // Check auth state
  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      setUser(user);
      if (user) {
        loadConversations();
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        loadConversations();
      } else {
        setConversations([]);
        setCurrentConversationId(null);
        setMessages([]);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Load conversations
  const loadConversations = async () => {
    setIsLoadingConversations(true);
    const { data, error } = await supabase
      .from('ai_conversations')
      .select('*')
      .order('updated_at', { ascending: false });
    
    if (error) {
      console.error('Error loading conversations:', error);
    } else {
      setConversations(data || []);
    }
    setIsLoadingConversations(false);
  };

  // Load messages for a conversation
  const loadMessages = async (conversationId: string) => {
    const { data, error } = await supabase
      .from('ai_messages')
      .select('*')
      .eq('conversation_id', conversationId)
      .order('created_at', { ascending: true });
    
    if (error) {
      console.error('Error loading messages:', error);
      return;
    }

    setMessages(data?.map(m => ({
      id: m.id,
      role: m.role as 'user' | 'assistant',
      content: m.content,
      image: m.image_url || undefined,
    })) || []);
    setCurrentConversationId(conversationId);
    setShowConversations(false);
  };

  // Create new conversation
  const createNewConversation = async (firstMessage: string) => {
    if (!user) return null;

    const title = firstMessage.slice(0, 50) + (firstMessage.length > 50 ? '...' : '');
    const { data, error } = await supabase
      .from('ai_conversations')
      .insert({ user_id: user.id, title })
      .select()
      .single();
    
    if (error) {
      console.error('Error creating conversation:', error);
      return null;
    }

    setConversations(prev => [data, ...prev]);
    setCurrentConversationId(data.id);
    return data.id;
  };

  // Save message to database
  const saveMessage = async (conversationId: string, message: Message) => {
    const { data, error } = await supabase
      .from('ai_messages')
      .insert({
        conversation_id: conversationId,
        role: message.role,
        content: message.content,
        image_url: message.image || null,
      })
      .select()
      .single();
    
    if (error) {
      console.error('Error saving message:', error);
      return null;
    }
    return data.id;
  };

  // Update message in database
  const updateMessage = async (messageId: string, content: string, imageUrl?: string) => {
    await supabase
      .from('ai_messages')
      .update({ content, image_url: imageUrl })
      .eq('id', messageId);
  };

  // Delete conversation
  const deleteConversation = async (conversationId: string) => {
    const { error } = await supabase
      .from('ai_conversations')
      .delete()
      .eq('id', conversationId);
    
    if (error) {
      toast.error('Errore eliminando la conversazione');
      return;
    }

    setConversations(prev => prev.filter(c => c.id !== conversationId));
    if (currentConversationId === conversationId) {
      setCurrentConversationId(null);
      setMessages([]);
    }
    toast.success('Conversazione eliminata');
  };

  // Start new chat
  const startNewChat = () => {
    setCurrentConversationId(null);
    setMessages([]);
    setShowConversations(false);
  };

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (inputRef.current && !showConversations) {
      inputRef.current.focus();
    }
  }, [showConversations]);

  const handleImageGeneration = async (prompt: string, messageId?: string) => {
    setIsGeneratingImage(true);
    try {
      const result = await generateImage(prompt);
      if (result.image) {
        setMessages(prev => {
          const newMessages = [...prev];
          const lastMessage = newMessages[newMessages.length - 1];
          if (lastMessage?.role === 'assistant') {
            lastMessage.content = lastMessage.content.replace(/\[GENERATE_IMAGE:.*?\]/g, '').trim();
            lastMessage.content += result.text ? `\n\n${result.text}` : '\n\n✨ Ecco l\'immagine che ho generato per te:';
            lastMessage.image = result.image;
            
            // Update in database
            if (messageId) {
              updateMessage(messageId, lastMessage.content, result.image);
            }
          }
          return newMessages;
        });
      }
    } catch (error) {
      toast.error("Errore nella generazione dell'immagine");
    } finally {
      setIsGeneratingImage(false);
    }
  };

  const handleVideoGeneration = async (prompt: string, messageId?: string) => {
    setIsGeneratingVideo(true);
    toast.info("🎬 Generazione frame cinematografico in corso...");
    try {
      const result = await generateVideo(prompt);
      if (result.image) {
        setMessages(prev => {
          const newMessages = [...prev];
          const lastMessage = newMessages[newMessages.length - 1];
          if (lastMessage?.role === 'assistant') {
            lastMessage.content = lastMessage.content.replace(/\[GENERATE_VIDEO:.*?\]/g, '').trim();
            lastMessage.content += result.text ? `\n\n${result.text}` : '\n\n🎬 Ecco il frame cinematografico generato:';
            lastMessage.image = result.image;
            
            if (messageId) {
              updateMessage(messageId, lastMessage.content, result.image);
            }
          }
          return newMessages;
        });
        toast.success("🎬 Frame generato con successo!");
      }
    } catch (error) {
      toast.error("Errore nella generazione del video");
    } finally {
      setIsGeneratingVideo(false);
    }
  };

  // Handle file selection
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Per favore seleziona un\'immagine');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('L\'immagine deve essere inferiore a 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target?.result as string;
      setPendingImage(base64);
    };
    reader.readAsDataURL(file);
    
    // Reset input
    e.target.value = '';
  };

  const removePendingImage = () => {
    setPendingImage(null);
  };

  const sendMessage = async () => {
    const trimmedInput = input.trim();
    if ((!trimmedInput && !pendingImage) || isLoading) return;

    const userMsg: Message = { 
      role: 'user', 
      content: trimmedInput || 'Analizza questa immagine',
      userImage: pendingImage || undefined
    };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setPendingImage(null);
    setIsLoading(true);

    let conversationId = currentConversationId;
    
    // Create conversation if user is logged in and no current conversation
    if (user && !conversationId) {
      conversationId = await createNewConversation(trimmedInput);
    }

    // Save user message
    if (conversationId) {
      await saveMessage(conversationId, userMsg);
    }

    let assistantSoFar = "";
    let assistantMessageId: string | null = null;

    const upsertAssistant = (nextChunk: string) => {
      assistantSoFar += nextChunk;
      setMessages(prev => {
        const last = prev[prev.length - 1];
        if (last?.role === 'assistant') {
          return prev.map((m, i) => (i === prev.length - 1 ? { ...m, content: assistantSoFar } : m));
        }
        return [...prev, { role: 'assistant', content: assistantSoFar }];
      });

      // Check for image generation trigger
      const imageMatch = assistantSoFar.match(/\[GENERATE_IMAGE:\s*(.+?)\]/);
      if (imageMatch && !isGeneratingImage) {
        handleImageGeneration(imageMatch[1], assistantMessageId || undefined);
      }

      // Check for video generation trigger
      const videoMatch = assistantSoFar.match(/\[GENERATE_VIDEO:\s*(.+?)\]/);
      if (videoMatch && !isGeneratingVideo) {
        handleVideoGeneration(videoMatch[1], assistantMessageId || undefined);
      }
    };

    try {
      await streamChat({
        messages: [...messages, userMsg],
        onDelta: (chunk) => upsertAssistant(chunk),
        onDone: async () => {
          setIsLoading(false);
          // Save assistant message
          if (conversationId && assistantSoFar) {
            const msgId = await saveMessage(conversationId, { role: 'assistant', content: assistantSoFar });
            assistantMessageId = msgId;
          }
        },
        onError: (error) => {
          toast.error(error);
          setIsLoading(false);
        },
      });
    } catch (e) {
      console.error(e);
      toast.error("Failed to send message");
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const renderMessageContent = (content: string) => {
    return content
      .replace(/\[GENERATE_IMAGE:.*?\]/g, '')
      .replace(/\[GENERATE_VIDEO:.*?\]/g, '')
      .trim();
  };

  // Conversations list view
  if (showConversations) {
    return (
      <motion.div
        initial={{ opacity: 0, x: -50 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -50 }}
        transition={{ duration: 0.3 }}
        className="flex flex-col h-full"
      >
        <div className="flex items-center gap-4 p-4 border-b border-border/30">
          <button
            onClick={() => setShowConversations(false)}
            className="p-2 rounded-full hover:bg-muted/30 transition-all duration-300 hover:scale-110 active:scale-90"
          >
            <ArrowLeft className="w-6 h-6 text-foreground" />
          </button>
          <h3 className="font-semibold text-lg">Cronologia Chat</h3>
        </div>

        <ScrollArea className="flex-1 p-4">
          {isLoadingConversations ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : conversations.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>Nessuna conversazione salvata</p>
            </div>
          ) : (
            <div className="space-y-2">
              {conversations.map((conv) => (
                <motion.div
                  key={conv.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center gap-2 group"
                >
                  <button
                    onClick={() => loadMessages(conv.id)}
                    className={`flex-1 text-left p-3 rounded-xl transition-all duration-200 ${
                      currentConversationId === conv.id 
                        ? 'bg-primary/10 border border-primary/30' 
                        : 'bg-muted/30 hover:bg-muted/50'
                    }`}
                  >
                    <p className="font-medium text-sm truncate">{conv.title}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {new Date(conv.created_at).toLocaleDateString('it-IT', {
                        day: 'numeric',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </button>
                  <button
                    onClick={() => deleteConversation(conv.id)}
                    className="p-2 rounded-full opacity-0 group-hover:opacity-100 hover:bg-destructive/10 transition-all"
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </button>
                </motion.div>
              ))}
            </div>
          )}
        </ScrollArea>

        <div className="p-4 border-t border-border/30">
          <Button
            onClick={startNewChat}
            className="w-full rounded-full bg-gradient-to-r from-primary to-blue-600"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nuova Chat
          </Button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 50 }}
      transition={{ duration: 0.3 }}
      className="flex flex-col h-full"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border/30">
        <div className="flex items-center gap-4">
          {onBack && (
            <button
              onClick={onBack}
              className="p-2 rounded-full hover:bg-muted/30 transition-all duration-300 hover:scale-110 active:scale-90"
            >
              <ArrowLeft className="w-6 h-6 text-foreground" />
            </button>
          )}
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-primary to-blue-600 flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-lg bg-gradient-to-r from-primary to-blue-600 bg-clip-text text-transparent">Tecly AI</h3>
              <p className="text-xs text-muted-foreground">Esperto di tecnologia • Genera immagini</p>
            </div>
          </div>
        </div>
        
        {user && (
          <button
            onClick={() => setShowConversations(true)}
            className="p-2 rounded-full hover:bg-muted/30 transition-all duration-300 hover:scale-110 active:scale-90"
          >
            <MessageSquare className="w-5 h-5 text-muted-foreground" />
          </button>
        )}
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 p-4" ref={scrollRef}>
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground py-12">
            <div className="h-20 w-20 rounded-full bg-gradient-to-br from-primary/20 to-blue-600/20 flex items-center justify-center mb-4">
              <Sparkles className="h-10 w-10 text-primary" />
            </div>
            <p className="text-lg font-medium mb-2">Ciao! Sono Tecly AI 🚀</p>
            <p className="text-sm max-w-xs">Esperto di tecnologia e prodotti Apple. Posso anche generare immagini per te!</p>
            <div className="flex flex-wrap gap-2 mt-4 justify-center">
              <span className="px-3 py-1 bg-primary/10 rounded-full text-xs">iPhone</span>
              <span className="px-3 py-1 bg-primary/10 rounded-full text-xs">Mac</span>
              <span className="px-3 py-1 bg-primary/10 rounded-full text-xs flex items-center gap-1">
                <ImageIcon className="h-3 w-3" /> Immagini AI
              </span>
            </div>
            {!user && (
              <p className="text-xs text-muted-foreground mt-6 bg-muted/30 px-4 py-2 rounded-full">
                Accedi per salvare le tue conversazioni
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-4 pb-4">
            {messages.map((msg, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {msg.role === 'assistant' && (
                  <div className="h-8 w-8 rounded-full bg-gradient-to-br from-primary to-blue-600 flex items-center justify-center flex-shrink-0 mt-1">
                    <Sparkles className="h-4 w-4 text-white" />
                  </div>
                )}
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm ${
                    msg.role === 'user'
                      ? 'bg-primary text-primary-foreground rounded-br-md'
                      : 'bg-muted/50 rounded-bl-md'
                  }`}
                >
                  {/* User's attached image */}
                  {msg.userImage && (
                    <motion.div 
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="mb-2"
                    >
                      <img 
                        src={msg.userImage} 
                        alt="Uploaded" 
                        className="rounded-xl max-w-full shadow-lg max-h-48 object-cover"
                      />
                    </motion.div>
                  )}
                  <p className="whitespace-pre-wrap break-words">{renderMessageContent(msg.content)}</p>
                  {/* AI generated image */}
                  {msg.image && (
                    <motion.div 
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="mt-3"
                    >
                      <img 
                        src={msg.image} 
                        alt="Generated" 
                        className="rounded-xl max-w-full shadow-lg"
                      />
                    </motion.div>
                  )}
                  {/* Video display */}
                  {msg.video && (
                    <motion.div 
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="mt-3"
                    >
                      <video 
                        src={msg.video} 
                        controls
                        autoPlay
                        loop
                        muted
                        playsInline
                        className="rounded-xl max-w-full shadow-lg"
                      />
                    </motion.div>
                  )}
                  {isGeneratingImage && msg === messages[messages.length - 1] && msg.role === 'assistant' && !msg.image && (
                    <div className="mt-3 flex items-center gap-2 text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span className="text-xs">Generando immagine...</span>
                    </div>
                  )}
                  {isGeneratingVideo && msg === messages[messages.length - 1] && msg.role === 'assistant' && !msg.video && (
                    <div className="mt-3 flex items-center gap-2 text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <Video className="h-4 w-4" />
                      <span className="text-xs">Generando video... (può richiedere 1-2 min)</span>
                    </div>
                  )}
                </div>
                {msg.role === 'user' && (
                  <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0 mt-1">
                    <User className="h-4 w-4 text-primary-foreground" />
                  </div>
                )}
              </motion.div>
            ))}
            {isLoading && messages[messages.length - 1]?.role === 'user' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex gap-3"
              >
                <div className="h-8 w-8 rounded-full bg-gradient-to-br from-primary to-blue-600 flex items-center justify-center">
                  <Loader2 className="h-4 w-4 text-white animate-spin" />
                </div>
                <div className="bg-muted/50 rounded-2xl rounded-bl-md px-4 py-3">
                  <div className="flex gap-1">
                    <span className="h-2 w-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="h-2 w-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="h-2 w-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        )}
      </ScrollArea>

      {/* Input */}
      <div className="p-4 border-t border-border/30 bg-background/50 backdrop-blur-sm">
        {/* Pending image preview */}
        {pendingImage && (
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-3 relative inline-block"
          >
            <img 
              src={pendingImage} 
              alt="To upload" 
              className="h-20 rounded-xl object-cover shadow-md"
            />
            <button
              onClick={removePendingImage}
              className="absolute -top-2 -right-2 h-6 w-6 bg-destructive rounded-full flex items-center justify-center text-white shadow-md hover:scale-110 transition-transform"
            >
              ×
            </button>
          </motion.div>
        )}
        
        <div className="flex gap-2 items-center">
          {/* Hidden file inputs */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleFileSelect}
            className="hidden"
          />
          
          {/* Gallery button */}
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={isLoading}
            size="icon"
            variant="ghost"
            className="rounded-full hover:bg-muted/50 flex-shrink-0"
          >
            <ImageLucide className="h-5 w-5 text-muted-foreground" />
          </Button>
          
          {/* Camera button */}
          <Button
            onClick={() => cameraInputRef.current?.click()}
            disabled={isLoading}
            size="icon"
            variant="ghost"
            className="rounded-full hover:bg-muted/50 flex-shrink-0"
          >
            <Camera className="h-5 w-5 text-muted-foreground" />
          </Button>
          
          <Input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Chiedimi di tech o invia una foto..."
            disabled={isLoading}
            className="flex-1 rounded-full bg-muted/30 border-border/50"
          />
          <Button
            onClick={sendMessage}
            disabled={(!input.trim() && !pendingImage) || isLoading}
            size="icon"
            className="rounded-full bg-gradient-to-r from-primary to-blue-600 hover:opacity-90 flex-shrink-0"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

export default AIChatbot;
